import { useCallback, useEffect, useState } from "react";
import { api, formatApiError, formatDate, formatAmount, formatFileSize } from "@/lib/api";
import { uploadWithProgress } from "@/lib/downloadWithProgress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { MoneyInput } from "@/components/MoneyInput";
import { DatePicker } from "@/components/DatePicker";
import { AlertTriangle, Camera, Download, FileText, Loader2, Mail, MessageCircle, Pencil, Plus, Send, Trash2, Upload, UserCheck, X } from "lucide-react";
import { toast } from "sonner";
import { EmailPreviewDialog, shouldSkipEmailPreview } from "@/components/EmailPreviewDialog";
import { ScanButton } from "@/components/ScanButton";
import { CameraButton } from "@/components/CameraButton";

const emptyForm = {
  event_date: new Date().toISOString().slice(0, 10),
  description: "",
  status: "aperto",
  claim_number: "",
  event_location: "",
  counterparty: "",
  estimated_amount: "",
  notes: "",
};

/**
 * ClaimsDialog: gestisce l'elenco sinistri di una singola polizza.
 * Props:
 *  - policy: oggetto polizza (id, policy_number, client_name, ...)
 *  - open: bool
 *  - onClose: () => void
 *  - onChanged?: () => void  (chiamato al primo cambiamento — utile per ricaricare i contatori del parent)
 */
export function ClaimsDialog({ policy, open, onClose, onChanged }) {
  const [loading, setLoading] = useState(false);
  const [claims, setClaims] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [editing, setEditing] = useState(null); // claim doc when editing existing
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [photosByClaim, setPhotosByClaim] = useState({});
  const [uploadingClaimId, setUploadingClaimId] = useState(null);
  // 🆕 Rename dialog: prima di caricare, l'utente può rinominare i file
  const [pendingUpload, setPendingUpload] = useState(null); // { claim, files: [{file, newName}] } | null
  // 🆕 Stato di hover per drag&drop per singolo claim
  const [dragOverClaimId, setDragOverClaimId] = useState(null);
  const [sendDialog, setSendDialog] = useState(null); // claim in fase di invio
  const [sendForm, setSendForm] = useState({ to_email: "", cc: "", subject: "", body: "", include_photos: true });
  const [sending, setSending] = useState(false);
  // 🆕 Anteprima email prima dell'invio (unificata via EmailPreviewDialog)
  const [emailPreviewCtx, setEmailPreviewCtx] = useState(null);
  // 🆕 Invito cliente a presentare denuncia in agenzia
  const [inviteDialog, setInviteDialog] = useState(null);
  const [inviteTemplates, setInviteTemplates] = useState([]);
  const [inviteForm, setInviteForm] = useState({ channel: "both", template_code: "standard", custom_text: "", to_email: "", to_mobile: "" });
  const [inviting, setInviting] = useState(false);

  const loadStatuses = useCallback(async () => {
    try {
      const { data } = await api.get("/lookup-lists/claim_status");
      setStatuses(Array.isArray(data) ? data : []);
    } catch {
      setStatuses([]);
    }
  }, []);

  const loadInviteTemplates = useCallback(async () => {
    try {
      const { data } = await api.get("/lookup-lists/claim_invite_templates");
      setInviteTemplates(Array.isArray(data) ? data : []);
    } catch {
      setInviteTemplates([]);
    }
  }, []);

  const loadClaims = useCallback(async () => {
    if (!policy?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/claims?policy_id=${policy.id}`);
      setClaims(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [policy?.id]);

  const loadPhotos = useCallback(async (claimId) => {
    try {
      const { data } = await api.get(`/claims/${claimId}/photos`);
      setPhotosByClaim((prev) => ({ ...prev, [claimId]: Array.isArray(data) ? data : [] }));
    } catch {
      setPhotosByClaim((prev) => ({ ...prev, [claimId]: [] }));
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadStatuses();
      loadInviteTemplates();
      loadClaims();
    } else {
      setShowForm(false);
      setEditing(null);
      setForm(emptyForm);
      setClaims([]);
      setPhotosByClaim({});
    }
  }, [open, loadStatuses, loadClaims]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      event_date: c.event_date || "",
      description: c.description || "",
      status: c.status || "aperto",
      claim_number: c.claim_number || "",
      event_location: c.event_location || "",
      counterparty: c.counterparty || "",
      estimated_amount: c.estimated_amount != null ? String(c.estimated_amount) : "",
      notes: c.notes || "",
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.event_date) return toast.error("Inserisci la data dell'evento");
    if (!form.description?.trim()) return toast.error("Inserisci una descrizione del sinistro");
    setSaving(true);
    try {
      const payload = {
        event_date: form.event_date,
        description: form.description.trim(),
        status: form.status || "aperto",
        claim_number: form.claim_number?.trim() || null,
        event_location: form.event_location?.trim() || null,
        counterparty: form.counterparty?.trim() || null,
        estimated_amount: form.estimated_amount === "" || form.estimated_amount == null
          ? null
          : Number(String(form.estimated_amount).replace(",", ".")),
        notes: form.notes?.trim() || null,
      };
      if (editing) {
        await api.patch(`/claims/${editing.id}`, payload);
        toast.success("Sinistro aggiornato");
      } else {
        await api.post("/claims", { ...payload, policy_id: policy.id });
        toast.success("Sinistro registrato");
      }
      setShowForm(false);
      setEditing(null);
      setForm(emptyForm);
      await loadClaims();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminare il sinistro del ${formatDate(c.event_date)}?`)) return;
    try {
      await api.delete(`/claims/${c.id}`);
      toast.success("Sinistro eliminato");
      await loadClaims();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onUploadPhoto = async (claim, file) => {
    // 🆕 Non caricare subito: apri dialog di rinomina
    if (!file) return;
    queueClaimUploads(claim, [file]);
  };

  // 🆕 Metti in coda uno o più file per il claim indicato: apre il dialog di rinomina
  const queueClaimUploads = (claim, files) => {
    const arr = Array.from(files || []).filter(Boolean);
    if (arr.length === 0) return;
    const items = arr.map((f) => ({ file: f, newName: f.name }));
    setPendingUpload({ claim, files: items });
  };

  // 🆕 Conferma upload: rinomina e carica tutti i file in coda
  const confirmClaimUpload = async () => {
    if (!pendingUpload) return;
    const { claim, files } = pendingUpload;
    setUploadingClaimId(claim.id);
    let ok = 0;
    try {
      for (const it of files) {
        const finalName = (it.newName || it.file.name).trim() || it.file.name;
        // Assicura che l'estensione originale sia mantenuta se l'utente l'ha rimossa
        const origExt = (it.file.name.split(".").pop() || "").toLowerCase();
        const hasExt = /\.[a-zA-Z0-9]{1,6}$/.test(finalName);
        const nameWithExt = hasExt ? finalName : (origExt ? `${finalName}.${origExt}` : finalName);
        const renamed = new File([it.file], nameWithExt, { type: it.file.type });
        try {
          const fd = new FormData();
          fd.append("file", renamed);
          await api.post(`/claims/${claim.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          ok++;
        } catch (err) {
          toast.error(`Upload "${nameWithExt}" fallito: ${formatApiError(err)}`);
        }
      }
      if (ok > 0) {
        toast.success(`${ok} file caricat${ok === 1 ? "o" : "i"} ✓`);
        await loadPhotos(claim.id);
        await loadClaims();
      }
    } finally {
      setUploadingClaimId(null);
      setPendingUpload(null);
    }
  };

  const deletePhoto = async (claimId, photoId) => {
    if (!window.confirm("Eliminare questa foto?")) return;
    try {
      await api.delete(`/claim-photos/${photoId}`);
      await loadPhotos(claimId);
      await loadClaims();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openSend = (c) => {
    setSendDialog(c);
    const co = c.claims_office || {};
    const preTo = co.email || co.pec || "";
    const preCc = (co.email && co.pec && co.email !== co.pec) ? co.pec : "";
    const notesLine = co.notes ? `\n\nNote ufficio sinistri: ${co.notes}` : "";
    setSendForm({
      to_email: preTo,
      cc: preCc,
      subject: `Denuncia Sinistro · ${c.claim_number || ""} · Polizza n° ${c.policy_number || ""}`,
      body: notesLine ? notesLine.trim() : "",
      include_photos: true,
    });
  };

  const submitSend = async () => {
    if (!sendDialog) return;
    if (!/^\S+@\S+\.\S+$/.test((sendForm.to_email || "").trim())) {
      return toast.error("Inserisci un'email destinatario valida");
    }
    const cc = (sendForm.cc || "").split(/[,;\s]+/).map((s) => s.trim()).filter((s) => /^\S+@\S+\.\S+$/.test(s));
    const payload = {
      to_email: sendForm.to_email.trim(),
      cc: cc.length ? cc : null,
      subject: sendForm.subject?.trim() || null,
      body: sendForm.body?.trim() || null,
      include_photos: !!sendForm.include_photos,
    };
    const doActualSend = async () => {
      setSending(true);
      try {
        const { data } = await api.post(`/claims/${sendDialog.id}/send-to-company`, payload);
        toast.success(`Denuncia inviata a ${data?.to || payload.to_email} (${data?.attachments_count || 0} allegati)`);
        setSendDialog(null);
        await loadClaims();
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setSending(false);
      }
    };
    if (shouldSkipEmailPreview("claim_to_company")) {
      await doActualSend();
      return;
    }
    // 🆕 Mostra prima l'anteprima
    setEmailPreviewCtx({
      typeKey: "claim_to_company",
      title: "Anteprima denuncia sinistro alla compagnia",
      fetchPreview: async () => {
        const { data } = await api.post(`/claims/${sendDialog.id}/send-to-company?dry_run=true`, payload);
        return data;
      },
      onConfirm: doActualSend,
    });
  };

  const openInvite = (c) => {
    setInviteDialog(c);
    setInviteForm({
      channel: "both",
      template_code: inviteTemplates[0]?.code || "standard",
      custom_text: "",
      to_email: "",
      to_mobile: "",
    });
  };

  const submitInvite = async () => {
    if (!inviteDialog) return;
    setInviting(true);
    try {
      const payload = {
        channel: inviteForm.channel,
        template_code: inviteForm.custom_text?.trim() ? null : (inviteForm.template_code || null),
        custom_text: inviteForm.custom_text?.trim() || null,
        to_email: inviteForm.to_email?.trim() || null,
        to_mobile: inviteForm.to_mobile?.trim() || null,
      };
      const { data } = await api.post(`/claims/${inviteDialog.id}/invite-client`, payload);
      // Se c'è un WA URL, lo apriamo in nuova tab
      const waSent = (data.channels_sent || []).find((c) => c.channel === "whatsapp");
      if (waSent?.wa_url) {
        window.open(waSent.wa_url, "_blank", "noopener,noreferrer");
      }
      const emailSent = (data.channels_sent || []).find((c) => c.channel === "email");
      const msgs = [];
      if (emailSent) msgs.push(`Email → ${emailSent.to}`);
      if (waSent) msgs.push(`WhatsApp aperto per ${waSent.to}`);
      toast.success(msgs.length ? msgs.join(" · ") : "Invito registrato");
      if (data.channels_failed?.length) {
        data.channels_failed.forEach((f) => toast.error(`${f.channel}: ${f.reason}`));
      }
      setInviteDialog(null);
      await loadClaims();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setInviting(false);
    }
  };

  const statusLabel = (code) => statuses.find((s) => s.code === code)?.label || code;

  const statusColor = (code) => {
    if (["chiuso", "liquidato"].includes(code)) return "bg-emerald-100 text-emerald-800 border-emerald-200";
    if (code === "respinto") return "bg-red-100 text-red-800 border-red-200";
    if (["in_istruttoria", "in_liquidazione"].includes(code)) return "bg-amber-100 text-amber-800 border-amber-200";
    return "bg-blue-100 text-blue-800 border-blue-200"; // aperto / denunciato
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="claims-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Sinistri · Polizza {policy?.policy_number || "—"}
          </DialogTitle>
          <DialogDescription>
            Gestisci i sinistri collegati a questa polizza — {policy?.client_name || ""}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between mb-3">
          <div className="text-sm text-slate-600">
            {loading ? "Caricamento…" : `${claims.length} sinistr${claims.length === 1 ? "o" : "i"} registrat${claims.length === 1 ? "o" : "i"}`}
          </div>
          {!showForm && (
            <Button size="sm" onClick={openCreate} data-testid="claim-new-btn" className="bg-amber-600 hover:bg-amber-700">
              <Plus className="h-4 w-4 mr-1" /> Segnala nuovo
            </Button>
          )}
        </div>

        {showForm && (
          <div className="border border-amber-200 bg-amber-50/60 rounded-lg p-4 mb-4 space-y-3" data-testid="claim-form">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-amber-900">
                {editing ? "Modifica sinistro" : "Nuovo sinistro"}
              </div>
              <button
                type="button"
                onClick={() => { setShowForm(false); setEditing(null); setForm(emptyForm); }}
                className="text-slate-400 hover:text-slate-700"
                aria-label="Chiudi"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Data evento *</Label>
                <DatePicker
                  value={form.event_date}
                  onChange={(e) => setForm((f) => ({ ...f, event_date: e.target.value }))}
                  data-testid="claim-event-date"
                />
              </div>
              <div>
                <Label className="text-xs">Stato *</Label>
                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger data-testid="claim-status-select"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                  <SelectContent>
                    {statuses.map((s) => (
                      <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Numero sinistro</Label>
                <Input
                  value={form.claim_number}
                  onChange={(e) => setForm((f) => ({ ...f, claim_number: e.target.value }))}
                  placeholder="Es. SIN-2026-001"
                  data-testid="claim-number-input"
                />
              </div>
              <div>
                <Label className="text-xs">Luogo evento</Label>
                <Input
                  value={form.event_location}
                  onChange={(e) => setForm((f) => ({ ...f, event_location: e.target.value }))}
                  placeholder="Es. Via Roma, Napoli"
                  data-testid="claim-location-input"
                />
              </div>
              <div>
                <Label className="text-xs">Controparte</Label>
                <Input
                  value={form.counterparty}
                  onChange={(e) => setForm((f) => ({ ...f, counterparty: e.target.value }))}
                  placeholder="Nome + targa/dati controparte"
                  data-testid="claim-counterparty-input"
                />
              </div>
              <div>
                <Label className="text-xs">Importo stimato (€)</Label>
                <MoneyInput
                  value={form.estimated_amount}
                  onChange={(v) => setForm((f) => ({ ...f, estimated_amount: v }))}
                  data-testid="claim-amount-input"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Descrizione del sinistro *</Label>
                <Textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Descrivi la dinamica dell'evento…"
                  data-testid="claim-description-input"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Note interne</Label>
                <Textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Annotazioni riservate…"
                  data-testid="claim-notes-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={() => { setShowForm(false); setEditing(null); setForm(emptyForm); }}>
                Annulla
              </Button>
              <Button size="sm" onClick={save} disabled={saving} data-testid="claim-save-btn" className="bg-amber-600 hover:bg-amber-700">
                {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                {editing ? "Aggiorna" : "Salva sinistro"}
              </Button>
            </div>
          </div>
        )}

        {!loading && claims.length === 0 && !showForm && (
          <div className="text-center py-8 text-slate-500 text-sm border border-dashed border-slate-200 rounded-lg">
            Nessun sinistro registrato per questa polizza.
          </div>
        )}

        <div className="space-y-3">
          {claims.map((c) => {
            const photos = photosByClaim[c.id];
            return (
              <div key={c.id} className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`claim-row-${c.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-slate-800">
                        {formatDate(c.event_date)}
                      </span>
                      {c.claim_number && (
                        <span className="text-xs px-2 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-600">
                          n° {c.claim_number}
                        </span>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded border ${statusColor(c.status)}`}>
                        {statusLabel(c.status)}
                      </span>
                      {c.estimated_amount != null && (
                        <span className="text-xs px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-800">
                          € {formatAmount(c.estimated_amount)}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-700 whitespace-pre-wrap">{c.description}</div>
                    <div className="text-xs text-slate-500 mt-1 space-x-3">
                      {c.event_location && <span>📍 {c.event_location}</span>}
                      {c.counterparty && <span>👤 {c.counterparty}</span>}
                    </div>
                    {c.notes && (
                      <div className="text-xs text-slate-500 mt-1 italic">Note: {c.notes}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <a
                      href={`${api.defaults.baseURL}/claims/${c.id}/denuncia-pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 p-1.5 rounded transition-colors"
                      title="Scarica denuncia sinistro PDF"
                      data-testid={`claim-pdf-${c.id}`}
                    >
                      <FileText className="h-5 w-5" />
                    </a>
                    <button
                      onClick={() => openSend(c)}
                      className="text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 p-1.5 rounded transition-colors"
                      title="Invia alla compagnia via email"
                      data-testid={`claim-send-${c.id}`}
                    >
                      <Send className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => openInvite(c)}
                      className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-1.5 rounded transition-colors"
                      title="Invita cliente a presentare denuncia in agenzia (Email + WhatsApp)"
                      data-testid={`claim-invite-${c.id}`}
                    >
                      <UserCheck className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => openEdit(c)}
                      className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-1.5 rounded transition-colors"
                      title="Modifica"
                      data-testid={`claim-edit-${c.id}`}
                    >
                      <Pencil className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => remove(c)}
                      className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1.5 rounded transition-colors"
                      title="Elimina"
                      data-testid={`claim-delete-${c.id}`}
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Foto — con drag&drop */}
                <div
                  className={`mt-3 border-2 border-dashed rounded-lg pt-2.5 px-3 pb-2 transition-all ${
                    dragOverClaimId === c.id
                      ? "border-blue-500 bg-blue-50 ring-4 ring-blue-300/50 scale-[1.01]"
                      : "border-slate-300 bg-slate-50/50 hover:border-blue-400 hover:bg-blue-50/30"
                  }`}
                  onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); if (Array.from(e.dataTransfer?.types || []).includes("Files")) setDragOverClaimId(c.id); }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = "copy"; }}
                  onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); if (dragOverClaimId === c.id) setDragOverClaimId(null); }}
                  onDrop={(e) => {
                    e.preventDefault(); e.stopPropagation(); setDragOverClaimId(null);
                    const files = Array.from(e.dataTransfer?.files || []);
                    if (files.length > 0) queueClaimUploads(c, files);
                  }}
                  data-testid={`claim-photos-dropzone-${c.id}`}
                >
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <div className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                      <Camera className="h-5 w-5 text-blue-600" />
                      Foto e documenti ({c.photos_count || 0})
                    </div>
                    <label className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded cursor-pointer inline-flex items-center gap-1.5 shadow-sm" data-testid={`claim-upload-${c.id}`}>
                      {uploadingClaimId === c.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4" />
                      )}
                      Aggiungi file
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          e.target.value = "";
                          queueClaimUploads(c, files);
                        }}
                      />
                    </label>
                    {/* 🆕 10/07/2026 — Import scanner / foto camera (allineato a Polizze) */}
                    <ScanButton
                      testid={`scan-claim-${c.id}`}
                      label="Importa da scanner"
                      variant="outline"
                      disabled={uploadingClaimId === c.id}
                      onFile={(f) => queueClaimUploads(c, [f])}
                    />
                    <CameraButton
                      testid={`cam-claim-${c.id}`}
                      label="📷 Foto da fotocamera"
                      variant="outline"
                      disabled={uploadingClaimId === c.id}
                      onFile={(f) => queueClaimUploads(c, [f])}
                    />
                  </div>
                  {/* 🆕 Hint drag&drop sempre visibile */}
                  <div className={`text-center py-2 rounded-md border border-dashed transition-colors ${
                    dragOverClaimId === c.id
                      ? "border-blue-500 bg-blue-100 text-blue-800 font-bold"
                      : "border-slate-300 bg-white text-slate-500"
                  }`}>
                    <div className="flex items-center justify-center gap-2 text-xs">
                      <Upload className={`h-4 w-4 ${dragOverClaimId === c.id ? "text-blue-700 animate-bounce" : "text-slate-400"}`} />
                      {dragOverClaimId === c.id
                        ? "↓ Rilascia qui per allegare a questo sinistro"
                        : "Trascina qui file (PDF/immagini) oppure usa 'Aggiungi file'"}
                    </div>
                  </div>
                  {photos === undefined ? (
                    (c.photos_count || 0) > 0 && (
                      <button
                        onClick={() => loadPhotos(c.id)}
                        className="text-xs text-blue-600 hover:underline mt-2"
                        data-testid={`claim-load-photos-${c.id}`}
                      >
                        Mostra {c.photos_count} foto/documenti →
                      </button>
                    )
                  ) : photos.length === 0 ? null : (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {photos.map((p) => (
                        <div key={p.id} className="border border-slate-200 rounded p-1.5 bg-white flex items-center gap-2" data-testid={`claim-photo-${p.id}`}>
                          <span className="text-xs text-slate-700 max-w-[160px] truncate" title={p.original_filename}>
                            {p.original_filename}
                          </span>
                          <a
                            href={`${api.defaults.baseURL}/claim-photos/${p.id}/download`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800"
                            title="Scarica/apri"
                          >
                            <Download className="h-4 w-4" />
                          </a>
                          <button
                            onClick={() => deletePhoto(c.id, p.id)}
                            className="text-red-500 hover:text-red-700"
                            title="Elimina"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>

      {/* 🆕 Dialog Invia a compagnia */}
      <Dialog open={!!sendDialog} onOpenChange={(o) => { if (!o && !sending) setSendDialog(null); }}>
        <DialogContent className="max-w-lg" data-testid="claim-send-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <Mail className="h-5 w-5" /> Invia denuncia alla compagnia
            </DialogTitle>
            <DialogDescription>
              Invieremo il PDF della denuncia sinistro {sendDialog?.include_photos && "insieme alle foto"} tramite l&apos;SMTP configurato per la tua agenzia.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {sendDialog?.claims_office?.email || sendDialog?.claims_office?.pec ? (
              <div className="text-xs bg-emerald-50 border border-emerald-200 rounded px-2.5 py-1.5 text-emerald-800">
                ✅ Email precompilata dalla rubrica ufficio sinistri di <strong>{sendDialog?.policy_company || "—"}</strong>
                {sendDialog?.claims_office?.phone && <span className="ml-1">· ☎ {sendDialog.claims_office.phone}</span>}
              </div>
            ) : (
              <div className="text-xs bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5 text-amber-800">
                ⚠️ Nessun contatto ufficio sinistri salvato per <strong>{sendDialog?.policy_company || "questa compagnia"}</strong>. Aggiungilo dalla sezione Compagnie per precompilare i prossimi invii.
              </div>
            )}
            <div>
              <Label className="text-xs">Email destinatario *</Label>
              <Input
                type="email"
                value={sendForm.to_email}
                onChange={(e) => setSendForm((f) => ({ ...f, to_email: e.target.value }))}
                placeholder="sinistri@compagnia.it"
                data-testid="claim-send-to"
              />
            </div>
            <div>
              <Label className="text-xs">CC (opzionale, separati da virgola)</Label>
              <Input
                value={sendForm.cc}
                onChange={(e) => setSendForm((f) => ({ ...f, cc: e.target.value }))}
                placeholder="cliente@example.com, broker@example.com"
                data-testid="claim-send-cc"
              />
            </div>
            <div>
              <Label className="text-xs">Oggetto</Label>
              <Input
                value={sendForm.subject}
                onChange={(e) => setSendForm((f) => ({ ...f, subject: e.target.value }))}
                data-testid="claim-send-subject"
              />
            </div>
            <div>
              <Label className="text-xs">Messaggio (opzionale — se vuoto viene usato un testo predefinito)</Label>
              <Textarea
                rows={4}
                value={sendForm.body}
                onChange={(e) => setSendForm((f) => ({ ...f, body: e.target.value }))}
                placeholder="Testo aggiuntivo per la compagnia…"
                data-testid="claim-send-body"
              />
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <input
                type="checkbox"
                checked={sendForm.include_photos}
                onChange={(e) => setSendForm((f) => ({ ...f, include_photos: e.target.checked }))}
                data-testid="claim-send-include-photos"
              />
              Allega anche le foto/documenti caricati ({sendDialog?.photos_count || 0})
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setSendDialog(null)} disabled={sending}>
                Annulla
              </Button>
              <Button
                size="sm"
                onClick={submitSend}
                disabled={sending}
                data-testid="claim-send-submit"
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {sending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                <Send className="h-4 w-4 mr-1" /> Invia denuncia
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 🆕 Dialog Invita cliente (email + WhatsApp) */}
      <Dialog open={!!inviteDialog} onOpenChange={(o) => { if (!o && !inviting) setInviteDialog(null); }}>
        <DialogContent className="max-w-lg" data-testid="claim-invite-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <UserCheck className="h-5 w-5" /> Invita cliente in agenzia
            </DialogTitle>
            <DialogDescription>
              Invia al cliente il messaggio per invitarlo a presentare la denuncia sinistro presso i tuoi uffici. Puoi personalizzare il testo o usare un template.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label className="text-xs">Canale</Label>
              <div className="flex items-center gap-2 mt-1">
                {["email", "whatsapp", "both"].map((ch) => (
                  <label key={ch} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded border text-xs cursor-pointer ${inviteForm.channel === ch ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-300 hover:bg-slate-50"}`}>
                    <input
                      type="radio"
                      name="invite-channel"
                      value={ch}
                      checked={inviteForm.channel === ch}
                      onChange={(e) => setInviteForm((f) => ({ ...f, channel: e.target.value }))}
                      className="sr-only"
                      data-testid={`claim-invite-channel-${ch}`}
                    />
                    {ch === "email" && <Mail className="h-3.5 w-3.5" />}
                    {ch === "whatsapp" && <MessageCircle className="h-3.5 w-3.5" />}
                    {ch === "both" && (<><Mail className="h-3.5 w-3.5" /><MessageCircle className="h-3.5 w-3.5" /></>)}
                    {ch === "email" ? "Email" : ch === "whatsapp" ? "WhatsApp" : "Entrambi"}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Template</Label>
              <Select
                value={inviteForm.template_code}
                onValueChange={(v) => setInviteForm((f) => ({ ...f, template_code: v, custom_text: "" }))}
              >
                <SelectTrigger data-testid="claim-invite-template"><SelectValue placeholder="Seleziona template…" /></SelectTrigger>
                <SelectContent>
                  {inviteTemplates.length === 0 ? (
                    <SelectItem value="standard">Standard</SelectItem>
                  ) : (
                    inviteTemplates.map((t) => (
                      <SelectItem key={t.code} value={t.code}>
                        {t.code.charAt(0).toUpperCase() + t.code.slice(1)}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {inviteTemplates.find((t) => t.code === inviteForm.template_code) && !inviteForm.custom_text && (
                <p className="text-[11px] text-slate-500 mt-1.5 whitespace-pre-wrap italic border-l-2 border-slate-200 pl-2">
                  {inviteTemplates.find((t) => t.code === inviteForm.template_code)?.label}
                </p>
              )}
              <p className="text-[10px] text-slate-400 mt-0.5">
                I template sono modificabili da <strong>Impostazioni → Liste Personalizzabili → &quot;claim_invite_templates&quot;</strong>.
              </p>
            </div>
            <div>
              <Label className="text-xs">Testo personalizzato (opzionale — sovrascrive il template)</Label>
              <Textarea
                rows={4}
                value={inviteForm.custom_text}
                onChange={(e) => setInviteForm((f) => ({ ...f, custom_text: e.target.value }))}
                placeholder="Se compilato, verrà usato al posto del template. Placeholder disponibili: {cliente}, {agenzia}, {telefono_agenzia}, {indirizzo_agenzia}, {polizza}, {compagnia}, {data_sinistro}"
                className="font-mono text-xs"
                data-testid="claim-invite-custom-text"
              />
            </div>
            {(inviteForm.channel === "email" || inviteForm.channel === "both") && (
              <div>
                <Label className="text-xs">Email destinatario (opzionale — default: email del cliente)</Label>
                <Input
                  type="email"
                  value={inviteForm.to_email}
                  onChange={(e) => setInviteForm((f) => ({ ...f, to_email: e.target.value }))}
                  placeholder="Vuoto = usa email del cliente"
                  data-testid="claim-invite-to-email"
                />
              </div>
            )}
            {(inviteForm.channel === "whatsapp" || inviteForm.channel === "both") && (
              <div>
                <Label className="text-xs">Numero WhatsApp (opzionale — default: cellulare del cliente)</Label>
                <Input
                  value={inviteForm.to_mobile}
                  onChange={(e) => setInviteForm((f) => ({ ...f, to_mobile: e.target.value }))}
                  placeholder="Vuoto = usa cellulare del cliente"
                  data-testid="claim-invite-to-mobile"
                />
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setInviteDialog(null)} disabled={inviting}>
                Annulla
              </Button>
              <Button
                size="sm"
                onClick={submitInvite}
                disabled={inviting}
                data-testid="claim-invite-submit"
                className="bg-blue-600 hover:bg-blue-700"
              >
                {inviting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                <Send className="h-4 w-4 mr-1" /> Invia invito
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 🆕 Dialog rinomina file prima di caricarli */}
      <Dialog open={!!pendingUpload} onOpenChange={(o) => { if (!o && !uploadingClaimId) setPendingUpload(null); }}>
        <DialogContent className="max-w-lg" data-testid="claim-rename-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Upload className="h-5 w-5" /> Rinomina file prima di caricare
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            <p className="text-xs text-slate-600">Personalizza il nome di ogni file. L&apos;estensione originale viene mantenuta automaticamente.</p>
            {pendingUpload?.files.map((it, idx) => (
              <div key={idx} className="border border-slate-200 rounded p-2 bg-slate-50 space-y-1" data-testid={`claim-rename-row-${idx}`}>
                <div className="text-[11px] text-slate-500 truncate" title={it.file.name}>
                  📄 Originale: <strong className="text-slate-700">{it.file.name}</strong> ({formatFileSize(it.file.size)})
                </div>
                <Input
                  data-testid={`claim-rename-input-${idx}`}
                  value={it.newName}
                  onChange={(e) => setPendingUpload((prev) => {
                    if (!prev) return prev;
                    const files = [...prev.files];
                    files[idx] = { ...files[idx], newName: e.target.value };
                    return { ...prev, files };
                  })}
                  className="text-sm h-8"
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingUpload(null)} disabled={!!uploadingClaimId} data-testid="claim-rename-cancel">
              Annulla
            </Button>
            <Button
              onClick={confirmClaimUpload}
              disabled={!!uploadingClaimId || !pendingUpload?.files.length}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="claim-rename-confirm"
            >
              {uploadingClaimId && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              <Upload className="h-4 w-4 mr-1" /> Carica {pendingUpload?.files.length ? `(${pendingUpload.files.length})` : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Dialog anteprima email prima dell'invio (denuncia sinistro alla compagnia) */}
      <EmailPreviewDialog
        open={!!emailPreviewCtx}
        onOpenChange={(o) => { if (!o) setEmailPreviewCtx(null); }}
        emailTypeKey={emailPreviewCtx?.typeKey}
        title={emailPreviewCtx?.title || "Anteprima email prima dell'invio"}
        fetchPreview={emailPreviewCtx?.fetchPreview || (async () => ({ subject: "", html_body: "", to: [] }))}
        onConfirmSend={async () => {
          if (emailPreviewCtx?.onConfirm) await emailPreviewCtx.onConfirm();
          setEmailPreviewCtx(null);
        }}
      />
    </Dialog>
  );
}

export default ClaimsDialog;
